/**
 * missingPersonsAdmin.tsx — Admin UI for the Missing Persons database.
 *
 * Two modes:
 *   - List mode (default): shows all missing persons, click to expand
 *   - Add mode: form to create a new entry, with face image upload
 *
 * Supports per-entry face management (add/remove face images).
 *
 * v0.4.9.2.3c22a (admin form polish):
 *   - Country field is now a strict alpha-3 SearchableSelect sourced from
 *     `src/data/countries.ts` ("Spain (ESP)" / "BLR — Belarus" pattern).
 *   - City field is now a SearchableSelect populated with the
 *     ISO-3166-2 subdivisions for the selected country (e.g. "BY-HM"
 *     → "Minsk (city)"). Subdivisions are loaded lazily via dynamic
 *     `import()` of `src/data/iso3166-2-data.ts` (~160 KB).
 *   - Country pre-fills with the admin's org country on mount.
 *   - Pencil edit button switched to blue (matches Most Wanted / stolen
 *     vehicles c22a polish).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type BoloTargetType } from '../services/api';
import { useTranslation } from 'react-i18next';
import { FiltersBar, applyFilters, type DateRange } from '../components/FiltersBar';
import { ArrowLeft, Plus, Trash2, Upload, ChevronDown, ChevronUp, X, Phone, Pencil } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { BoloTab } from '../components/BoloTab';
import { SearchableSelect, countryListToOptions } from '../components/SearchableSelect';
import { COUNTRIES } from '../data/countries';
import { getSubdivisions, type Subdivision } from '../utils/iso3166-2';
import { useUserOrgCountry } from '../hooks/useUserOrgCountry';

const TAB_TARGET_TYPE: BoloTargetType = 'missing_person';

export default function MissingPersonsAdmin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [faces, setFaces] = useState<Record<string, any[]>>({});
  // v0.4.9.2.3c22ca: search + date-range filter on the records tab.
  // Default = last 7 days; in-memory state.
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const start = new Date(); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
    return { start, end };
  });
  // v0.4.9.2.2: BOLO tab
  const [activeTab, setActiveTab] = useState<'records' | 'bolo'>('records');
  const [availableOrgs, setAvailableOrgs] = useState<Array<{ id: string; name: string; country: string }>>([]);
  const isAgentAdmin = user?.role === 'agentAdmin' || user?.role === 'superadmin';
  const currentOrgId = user?.civil_respondent_id ?? null;

  // v0.4.9.2.3c22a: pre-fill country with the admin's org country on mount.
  const orgCountry = useUserOrgCountry();
  // v0.4.9.2.3c22a: subdivisions loaded lazily per selected country; used by
  // the city dropdown. `null` until the user picks a country.
  const [formSubs, setFormSubs] = useState<Subdivision[]>([]);
  const [formSubsLoading, setFormSubsLoading] = useState(false);
  const [editSubs, setEditSubs] = useState<Subdivision[]>([]);
  const [editSubsLoading, setEditSubsLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    full_name: '', age: '', gender: '', description: '',
    country: '', city: '', contact_name: '', contact_phone: '', contact_relation: '',
    notes: '',
  });
  const [faceFile, setFaceFile] = useState<File | null>(null);
  const [facePreview, setFacePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [editing, setEditing] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({
    full_name: '', age: '', gender: '', description: '',
    country: '', city: '', contact_name: '', contact_phone: '', contact_relation: '',
    notes: '', status: 'active',
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  function load() {
    setLoading(true);
    api.listMissingPersons()
      .then(setList)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  // v0.4.9.2.3c22a: pre-fill form.country with org country on mount.
  useEffect(() => {
    if (orgCountry && !form.country) {
      setForm(f => ({ ...f, country: orgCountry }));
    }
  }, [orgCountry]);

  // v0.4.9.2.3c22a: when country changes in the Add form, load that country's
  // subdivisions for the city dropdown. We do NOT auto-clear the city if the
  // stored value isn't in the new country's list — legacy data may have full
  // names ("Madrid") or codes not in our subset, and the admin should be
  // able to see + correct them via displayFallback rather than have us silently
  // wipe their values.
  useEffect(() => {
    let cancelled = false;
    setFormSubsLoading(true);
    if (!form.country) { setFormSubs([]); setFormSubsLoading(false); return; }
    getSubdivisions(form.country).then(subs => {
      if (cancelled) return;
      setFormSubs(subs);
      setFormSubsLoading(false);
    });
    return () => { cancelled = true; };
  }, [form.country]);

  // v0.4.9.2.3c22a: same for the Edit modal — subdivisions follow the
  // edit form's selected country.
  useEffect(() => {
    let cancelled = false;
    setEditSubsLoading(true);
    if (!editForm.country) { setEditSubs([]); setEditSubsLoading(false); return; }
    getSubdivisions(editForm.country).then(subs => {
      if (cancelled) return;
      setEditSubs(subs);
      setEditSubsLoading(false);
    });
    return () => { cancelled = true; };
  }, [editForm.country]);

  async function loadFaces(personId: string) {
    if (faces[personId]) return;
    const f = await api.listMissingPersonFaces(personId).catch(() => []);
    setFaces(prev => ({ ...prev, [personId]: f }));
  }

  // v0.4.9.2.3c22ca: derive filtered record list from search + date range.
  // Searches across name, country, city, description, notes, contact info.
  const filteredList = applyFilters(
    list,
    search,
    dateRange,
    (p: any) => p.created_at,
    (p: any) =>
      [
        p.full_name,
        p.country,
        p.city,
        p.description,
        p.notes,
        p.contact_info?.name,
        p.contact_info?.phone,
        p.contact_info?.relation,
      ]
        .filter(Boolean)
        .join(' ')
  );

  function resetForm() {
    setForm({
      full_name: '', age: '', gender: '', description: '',
      country: '', city: '', contact_name: '', contact_phone: '', contact_relation: '',
      notes: '',
    });
    setFaceFile(null);
    setFacePreview(null);
    setError(null);
  }

  function onFaceFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaceFile(file);
    const reader = new FileReader();
    reader.onload = () => setFacePreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  async function submit() {
    if (!form.full_name || !form.country) {
      setError(t('records.err.nameCountryRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const contact_info = form.contact_name || form.contact_phone || form.contact_relation
        ? {
            name: form.contact_name || undefined,
            phone: form.contact_phone || undefined,
            relation: form.contact_relation || undefined,
          }
        : undefined;
      const created = await api.createMissingPerson({
        full_name: form.full_name,
        age: form.age ? parseInt(form.age) : undefined,
        gender: form.gender || undefined,
        description: form.description || undefined,
        country: form.country,
        city: form.city || undefined,
        contact_info,
        notes: form.notes || undefined,
      });
      if (faceFile) {
        const b64 = await fileToBase64(faceFile);
        await api.addMissingPersonFace(created.id, b64);
      }
      setShowAdd(false);
      resetForm();
      load();
    } catch (err: any) {
      setError(err.message || t('records.err.createFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  async function addExtraFace(personId: string) {
    fileInputRef.current?.click();
    fileInputRef.current!.onchange = async () => {
      const file = fileInputRef.current?.files?.[0];
      if (!file) return;
      try {
        const b64 = await fileToBase64(file);
        await api.addMissingPersonFace(personId, b64);
        // Refresh faces
        const f = await api.listMissingPersonFaces(personId);
        setFaces(prev => ({ ...prev, [personId]: f }));
        // Reset the input
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err: any) {
        alert('Failed to add face: ' + err.message);
      }
    };
  }

  async function deleteFace(personId: string, faceId: string) {
    if (!confirm(t('records.confirmDeleteFace'))) return;
    try {
      await api.deleteMissingPersonFace(personId, faceId);
      const f = await api.listMissingPersonFaces(personId);
      setFaces(prev => ({ ...prev, [personId]: f }));
    } catch (err: any) {
      alert('Failed: ' + err.message);
    }
  }

  async function deletePerson(id: string) {
    if (!confirm(t('mp.confirmDeleteEntry'))) return;
    try {
      await api.deleteMissingPerson(id);
      load();
    } catch (err: any) {
      alert('Failed: ' + err.message);
    }
  }

  function startEdit(p: any) {
    setEditing(p);
    setEditForm({
      full_name: p.full_name || '',
      age: p.age != null ? String(p.age) : '',
      gender: p.gender || '',
      description: p.description || '',
      country: p.country || '',
      city: p.city || '',
      contact_name: p.contact_info?.name || '',
      contact_phone: p.contact_info?.phone || '',
      contact_relation: p.contact_info?.relation || '',
      notes: p.notes || '',
      status: p.status || 'active',
    });
  }

  async function submitEdit() {
    if (!editing) return;
    if (!editForm.full_name || !editForm.country) {
      alert(t('records.err.nameCountryRequired'));
      return;
    }
    setEditSubmitting(true);
    try {
      const contact_info = editForm.contact_name || editForm.contact_phone || editForm.contact_relation
        ? {
            name: editForm.contact_name || undefined,
            phone: editForm.contact_phone || undefined,
            relation: editForm.contact_relation || undefined,
          }
        : undefined;
      await api.updateMissingPerson(editing.id, {
        full_name: editForm.full_name,
        age: editForm.age ? parseInt(editForm.age) : undefined,
        gender: editForm.gender || undefined,
        description: editForm.description || undefined,
        country: editForm.country,
        city: editForm.city || undefined,
        contact_info,
        notes: editForm.notes || undefined,
        status: editForm.status,
      });
      setEditing(null);
      load();
    } catch (err: any) {
      alert('Failed: ' + err.message);
    } finally {
      setEditSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="px-4 py-3 bg-card border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/civil-respondent')} className="text-muted-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-foreground">{t('civil.missing.title')}</h1>
        </div>
        <button
          onClick={() => setShowAdd(s => !s)}
          className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
        >
          {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAdd ? t('common.cancel') : t('common.add')}
        </button>
      </header>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" />

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl p-5 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground">{t('civil.missing.editTitle')}</h2>
              <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label={t('records.field.fullName')} value={editForm.full_name} onChange={v => setEditForm({ ...editForm, full_name: v })} />
              {/* v0.4.9.2.3c22a: strict alpha-3 country dropdown for edit. */}
              <SearchableSelect
                label={t('records.field.country')}
                value={editForm.country}
                onChange={v => setEditForm({ ...editForm, country: v })}
                options={countryListToOptions(COUNTRIES)}
                placeholder={t('records.field.searchCountry')}
                required
              />
              {/* v0.4.9.2.3c22a: city dropdown filtered by edit country.
                  displayFallback handles legacy free-text / unmapped codes. */}
              <SearchableSelect
                label={t('records.field.city')}
                value={editForm.city}
                displayFallback={editForm.city}
                onChange={v => setEditForm({ ...editForm, city: v })}
                options={editSubs.map(s => ({ value: s.code, label: s.name, hint: s.code }))}
                placeholder={editForm.country ? t('records.field.searchCity') : t('records.field.pickCountryFirst')}
                loading={editSubsLoading}
              />
              <Input label={t('mp.field.age')} type="number" value={editForm.age} onChange={v => setEditForm({ ...editForm, age: v })} />
              <Select label={t('mp.field.gender')} value={editForm.gender} onChange={v => setEditForm({ ...editForm, gender: v })}
                options={[{ v: '', l: '—' }, { v: 'M', l: t('mp.gender.male') }, { v: 'F', l: t('mp.gender.female') }, { v: 'O', l: t('mp.gender.other') }]} />
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wide">{t('civil.common.status')}</label>
                <select
                  value={editForm.status}
                  onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-input rounded text-sm bg-background"
                >
                  <option value="active">active</option>
                  <option value="found">found</option>
                  <option value="closed">closed</option>
                </select>
              </div>
              <Input label={t('mp.field.description')} value={editForm.description} onChange={v => setEditForm({ ...editForm, description: v })} className="md:col-span-2" />
            </div>

            <h3 className="text-sm font-semibold text-foreground mt-5 mb-2">{t('mp.section.contact')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label={t('mp.field.contactName')} value={editForm.contact_name} onChange={v => setEditForm({ ...editForm, contact_name: v })} />
              <Input label={t('mp.field.contactPhone')} value={editForm.contact_phone} onChange={v => setEditForm({ ...editForm, contact_phone: v })} />
              <Input label={t('mp.field.contactRelation')} value={editForm.contact_relation} onChange={v => setEditForm({ ...editForm, contact_relation: v })} />
            </div>

            <Input label={t('records.field.notes')} value={editForm.notes} onChange={v => setEditForm({ ...editForm, notes: v })} className="mt-3" />
            <p className="text-xs text-muted-foreground mt-3">
              {t('mp.face.manageHint')}
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 border border-input rounded-lg hover:bg-accent text-sm"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={submitEdit}
                disabled={editSubmitting}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm"
              >
                {editSubmitting ? t('common.saving') : t('records.action.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto p-4">
        {isAgentAdmin && (
          <div className="border-b border-border flex gap-1 mb-4">
            <button
              type="button"
              onClick={() => setActiveTab('records')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'records'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('records.tabRecords')}
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('bolo');
                // SuperAdmin: fetch available orgs the first time the BOLO tab is opened
                if (user?.role === 'superadmin' && availableOrgs.length === 0) {
                  api.listCivilRespondents().then((orgs: any[]) => {
                    setAvailableOrgs(
                      orgs.map((o) => ({
                        id: o.id,
                        name: o.division_name || o.name || o.id,
                        country: o.country || '',
                      }))
                    );
                  }).catch(() => {});
                }
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'bolo'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('records.tabBolo')}
            </button>
          </div>
        )}

        {activeTab === 'records' && showAdd && (
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm mb-4">
            <h2 className="font-semibold text-foreground mb-4">{t('civil.missing.newTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label={t('records.field.fullName')} value={form.full_name} onChange={v => setForm({ ...form, full_name: v })} />
              {/* v0.4.9.2.3c22a: strict alpha-3 country dropdown. */}
              <SearchableSelect
                label={t('records.field.country')}
                value={form.country}
                onChange={v => setForm({ ...form, country: v })}
                options={countryListToOptions(COUNTRIES)}
                placeholder={t('records.field.searchCountry')}
                required
              />
              {/* v0.4.9.2.3c22a: city is now ISO-3166-2 dropdown filtered by country.
                  displayFallback shows legacy values ("Madrid", "BY-HM") when the
                  stored city doesn't match a current subdivision option. */}
              <SearchableSelect
                label={t('records.field.city')}
                value={form.city}
                displayFallback={form.city}
                onChange={v => setForm({ ...form, city: v })}
                options={formSubs.map(s => ({ value: s.code, label: s.name, hint: s.code }))}
                placeholder={form.country ? t('records.field.searchCity') : t('records.field.pickCountryFirst')}
                loading={formSubsLoading}
              />
              <Input label={t('mp.field.age')} type="number" value={form.age} onChange={v => setForm({ ...form, age: v })} />
              <Select label={t('mp.field.gender')} value={form.gender} onChange={v => setForm({ ...form, gender: v })}
                options={[{ v: '', l: '—' }, { v: 'M', l: t('mp.gender.male') }, { v: 'F', l: t('mp.gender.female') }, { v: 'O', l: t('mp.gender.other') }]} />
              <Input label={t('mp.field.description')} value={form.description} onChange={v => setForm({ ...form, description: v })} />
            </div>

            <h3 className="text-sm font-semibold text-foreground mt-5 mb-2">{t('mp.section.contact')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input label={t('mp.field.contactName')} value={form.contact_name} onChange={v => setForm({ ...form, contact_name: v })} />
              <Input label={t('mp.field.contactPhone')} value={form.contact_phone} onChange={v => setForm({ ...form, contact_phone: v })} />
              <Input label={t('mp.field.contactRelation')} value={form.contact_relation} onChange={v => setForm({ ...form, contact_relation: v })} />
            </div>

            <Input label={t('records.field.notes')} value={form.notes} onChange={v => setForm({ ...form, notes: v })} className="mt-3" />

            <div className="mt-4">
              <label className="text-xs text-muted-foreground uppercase tracking-wide">{t('mp.face.required')}</label>
              <div className="mt-2 flex items-center gap-3">
                {facePreview ? (
                  <img src={facePreview} alt={t('records.alt.facePreview')} className="w-20 h-20 object-cover rounded" />
                ) : (
                  <div className="w-20 h-20 bg-accent rounded flex items-center justify-center text-xs text-muted-foreground">{t('civil.common.noImage')}</div>
                )}
                <label className="cursor-pointer px-3 py-2 border border-input rounded text-sm hover:bg-background">
                  {faceFile ? t('records.face.change') : t('records.face.chooseFile')}
                  <input type="file" accept="image/*" onChange={onFaceFile} className="hidden" />
                </label>
                {faceFile && <span className="text-xs text-muted-foreground">{faceFile.name}</span>}
              </div>
            </div>

            {error && (
              <div className="mt-3 p-2 bg-destructive/10 border border-destructive/30 text-destructive rounded text-sm">
                {error}
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {submitting ? t('common.saving') : t('common.save')}
              </button>
              <button
                onClick={() => { setShowAdd(false); resetForm(); }}
                className="px-4 py-2 border border-input rounded-lg hover:bg-background"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'records' && (
          <>
        {loading ? (
          <div className="text-muted-foreground text-sm py-8 text-center">{t('common.loading')}</div>
        ) : list.length === 0 ? (
          <div className="text-muted-foreground text-sm py-8 text-center">{t('records.empty.missingPersons')}</div>
        ) : (
          <>
            {/* v0.4.9.2.3c22ca: search + date-range filter strip */}
            <FiltersBar
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by name, city, country, description…"
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            {filteredList.length === 0 ? (
              <div className="text-muted-foreground text-sm py-8 text-center">
                {t('records.empty.noMatches')}
              </div>
            ) : (
            <div className="space-y-3">
              {filteredList.map((p) => {
              const isExpanded = expandedId === p.id;
              return (
                <div key={p.id} className="bg-card border border-border rounded-lg shadow-sm">
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-foreground">{p.full_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.country}{p.city ? ` · ${p.city}` : ''}
                        {p.age ? ` · ${t('records.label.age')} ${p.age}` : ''}
                        {p.gender ? ` · ${p.gender}` : ''}
                        {p.status !== 'active' ? ` · ${p.status}` : ''}
                      </div>
                      {p.contact_info?.phone && (
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {p.contact_info.name || t('records.field.contact')}: {p.contact_info.phone}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setExpandedId(isExpanded ? null : p.id);
                          if (!isExpanded) loadFaces(p.id);
                        }}
                        className="p-2 text-muted-foreground hover:bg-accent rounded"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {/* v0.4.9.2.3c22a: blue pencil to match Most Wanted / c22a fix #1. */}
                      <button
                        onClick={() => startEdit(p)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('records.title.editEntry')}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePerson(p.id)}
                        className="p-2 text-red-500 hover:bg-destructive/10 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-border pt-3">
                      {p.description && <p className="text-sm text-foreground mb-2">{p.description}</p>}
                      {p.notes && <p className="text-xs text-muted-foreground italic mb-2">{p.notes}</p>}

                      <div className="text-xs text-muted-foreground uppercase tracking-wide mt-3 mb-2">{t('civil.common.faceImages')}</div>
                      <div className="grid grid-cols-3 gap-2">
                        {(faces[p.id] || []).map((f) => (
                          <div key={f.id} className="relative">
                            <img src={f.photo_url} alt={t('mp.alt.face')} className="w-full aspect-square object-cover rounded" />
                            <button
                              onClick={() => deleteFace(p.id, f.id)}
                              className="absolute top-1 right-1 p-1 bg-destructive/100 text-white rounded-full"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addExtraFace(p.id)}
                          className="aspect-square border-2 border-dashed border-input rounded flex flex-col items-center justify-center text-muted-foreground hover:border-foreground/40 hover:text-muted-foreground"
                        >
                          <Upload className="w-5 h-5" />
                          <span className="text-xs mt-1">{t('civil.common.addFace')}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
            )}
          </>
        )}
          </>
        )}

        {activeTab === 'bolo' && isAgentAdmin && (
          <BoloTab
            targetType={TAB_TARGET_TYPE}
            orgId={currentOrgId ?? undefined}
            availableOrgs={user?.role === 'superadmin' ? availableOrgs : undefined}
            records={list.map((r) => ({
              id: r.id,
              label: r.full_name,
              country: r.country,
              photoUrl: null,
            }))}
          />
        )}
      </main>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Input({ label, value, onChange, type = 'text', className = '' }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; className?: string;
}) {
  return (
    <div className={className}>
      <label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="mt-1 block w-full px-3 py-2 border border-input rounded text-sm focus:outline-none focus:border-primary"
      />
    </div>
  );
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { v: string; l: string }[];
}) {
  return (
    <div>
      <label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="mt-1 block w-full px-3 py-2 border border-input rounded text-sm focus:outline-none focus:border-primary"
      >
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
